import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Clock,
  User,
  Pencil,
  Trash2,
  AlertTriangle,
  CheckCircle2,
  ListChecks,
  History,
  Link2,
  Check,
  Printer,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useDashboard } from "@/context/DashboardContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { CodeBlock } from "@/components/CodeBlock";
import DeleteRunbookDialog from "@/components/DeleteRunbookDialog";
import { ErrorState } from "@/components/states";
import { Skeleton } from "@/components/ui/skeleton";
import { SEVERITY_META, categoryIcon, OUTCOME_META } from "@/lib/meta";
import { RUNBOOK } from "@/constants/testIds";
import { cn } from "@/lib/utils";

export default function RunbookDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { canEdit } = useAuth();
  const { reload } = useDashboard();
  const [runbook, setRunbook] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [checked, setChecked] = useState({});
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const [executions, setExecutions] = useState([]);
  const [outcome, setOutcome] = useState("success");
  const [notes, setNotes] = useState("");
  const [savingOutcome, setSavingOutcome] = useState(false);
  const [copied, setCopied] = useState(false);

  const copyLink = async () => {
    const url = window.location.href;
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      const ta = document.createElement("textarea");
      ta.value = url;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
    }
    setCopied(true);
    toast.success("Link copied to clipboard");
    setTimeout(() => setCopied(false), 2000);
  };

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.get(`/runbooks/${id}`);
      setRunbook(data);
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [id]);

  const loadExecutions = useCallback(async () => {
    try {
      const { data } = await api.get(`/runbooks/${id}/executions`);
      setExecutions(data.executions);
    } catch (e) {
      console.warn("Failed to load runbook executions (non-blocking):", formatApiError(e));
    }
  }, [id]);

  useEffect(() => {
    load();
    loadExecutions();
  }, [load, loadExecutions]);

  // Keep each public runbook URL search-engine friendly with unique metadata.
  useEffect(() => {
    if (!runbook) return undefined;

    const siteName = "InfraPilot";
    const canonicalUrl = `${window.location.origin}/runbooks/${encodeURIComponent(runbook.id)}`;
    const description = (runbook.summary || `${runbook.title} - infrastructure runbook on InfraPilot.`).trim().slice(0, 160);
    const title = `${runbook.title} | InfraPilot`;

    document.title = title;

    const upsertMeta = (selector, attributes, content) => {
      let el = document.head.querySelector(selector);
      if (!el) {
        el = document.createElement("meta");
        Object.entries(attributes).forEach(([key, value]) => el.setAttribute(key, value));
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };

    const upsertLink = (selector, rel, href) => {
      let el = document.head.querySelector(selector);
      if (!el) {
        el = document.createElement("link");
        el.setAttribute("rel", rel);
        document.head.appendChild(el);
      }
      el.setAttribute("href", href);
    };

    upsertMeta('meta[name="description"]', { name: "description" }, description);
    upsertMeta('meta[name="robots"]', { name: "robots" }, "index,follow,max-image-preview:large");
    upsertMeta('meta[property="og:title"]', { property: "og:title" }, title);
    upsertMeta('meta[property="og:description"]', { property: "og:description" }, description);
    upsertMeta('meta[property="og:url"]', { property: "og:url" }, canonicalUrl);
    upsertMeta('meta[property="og:type"]', { property: "og:type" }, "article");
    upsertMeta('meta[property="og:site_name"]', { property: "og:site_name" }, siteName);
    upsertMeta('meta[name="twitter:card"]', { name: "twitter:card" }, "summary");
    upsertMeta('meta[name="twitter:title"]', { name: "twitter:title" }, title);
    upsertMeta('meta[name="twitter:description"]', { name: "twitter:description" }, description);
    upsertLink('link[rel="canonical"]', "canonical", canonicalUrl);

    const schemaId = "infrapilot-runbook-schema";
    let schema = document.getElementById(schemaId);
    if (!schema) {
      schema = document.createElement("script");
      schema.id = schemaId;
      schema.type = "application/ld+json";
      document.head.appendChild(schema);
    }
    schema.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "TechArticle",
      headline: runbook.title,
      description,
      url: canonicalUrl,
      dateModified: runbook.updated_at,
      author: { "@type": "Person", name: runbook.owner || "InfraPilot" },
      publisher: { "@type": "Organization", name: siteName, url: window.location.origin },
      articleSection: runbook.category,
      keywords: Array.isArray(runbook.tags) ? runbook.tags.join(", ") : "",
    });

    return undefined;
  }, [runbook]);

  const steps = runbook?.steps || [];
  const doneCount = useMemo(() => Object.values(checked).filter(Boolean).length, [checked]);
  const progress = steps.length ? Math.round((doneCount / steps.length) * 100) : 0;

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await api.delete(`/runbooks/${id}`);
      toast.success("Runbook deleted");
      reload();
      navigate("/");
    } catch (e) {
      toast.error(formatApiError(e));
      setDeleting(false);
      setDeleteOpen(false);
    }
  };

  const submitOutcome = async () => {
    setSavingOutcome(true);
    try {
      await api.post(`/runbooks/${id}/executions`, { status: outcome, notes });
      toast.success("Outcome recorded");
      setNotes("");
      loadExecutions();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSavingOutcome(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6 max-w-4xl">
        <Skeleton className="h-6 w-24" />
        <Skeleton className="h-10 w-3/4" />
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-40 w-full" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-3xl">
        <Button variant="ghost" onClick={() => navigate("/")} className="gap-2 mb-4" data-testid={RUNBOOK.backBtn}>
          <ArrowLeft className="h-4 w-4" /> Back to library
        </Button>
        <ErrorState message={error} onRetry={load} />
      </div>
    );
  }

  const sev = SEVERITY_META[runbook.severity] || SEVERITY_META.P3;
  const Icon = categoryIcon(runbook.category);

  return (
    <div data-testid={RUNBOOK.detail} className="max-w-5xl mx-auto space-y-8 tr-fade-up">
      <Button variant="ghost" onClick={() => navigate("/")} className="gap-2 no-print" data-testid={RUNBOOK.backBtn}>
        <ArrowLeft className="h-4 w-4" /> Back to library
      </Button>

      {/* Metadata header */}
      <div className="space-y-4">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="space-y-3">
            <div className="flex items-center gap-2 flex-wrap">
              <Badge variant="outline" className={cn("border", sev.badge)}>{sev.label}</Badge>
              <span className="inline-flex items-center gap-1.5 text-sm text-muted-foreground">
                <Icon className="h-4 w-4" /> {runbook.category}
              </span>
              <Badge variant="outline" className="border-border">{runbook.environment}</Badge>
            </div>
            <h1 className="font-display text-3xl font-bold tracking-tight">{runbook.title}</h1>
          </div>
          <div className="flex gap-2 flex-wrap no-print">
            <Button variant="outline" className="gap-2" onClick={copyLink} data-testid="runbook-share-btn">
              {copied ? <Check className="h-4 w-4 text-emerald-500" /> : <Link2 className="h-4 w-4" />} {copied ? "Copied" : "Copy link"}
            </Button>
            <Button variant="outline" className="gap-2" onClick={() => window.print()} data-testid="runbook-print-btn">
              <Printer className="h-4 w-4" /> Print
            </Button>
            {canEdit(runbook) && (
              <>
                <Button variant="outline" className="gap-2" onClick={() => navigate(`/runbooks/${id}/edit`)} data-testid={RUNBOOK.editBtn}>
                  <Pencil className="h-4 w-4" /> Edit
                </Button>
                <Button variant="outline" className="gap-2 text-red-500 hover:text-red-500 border-red-500/30 hover:bg-red-500/10" onClick={() => setDeleteOpen(true)} data-testid={RUNBOOK.deleteBtn}>
                  <Trash2 className="h-4 w-4" /> Delete
                </Button>
              </>
            )}
          </div>
        </div>

        {runbook.summary && <p className="text-base text-muted-foreground leading-relaxed max-w-3xl whitespace-pre-wrap">{runbook.summary}</p>}

        <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-muted-foreground border-y border-border py-3">
          <span className="inline-flex items-center gap-1.5"><Clock className="h-4 w-4" /> {runbook.estimated_time || "—"}</span>
          <span className="inline-flex items-center gap-1.5"><User className="h-4 w-4" /> {runbook.owner}</span>
          <span className="text-xs">Updated {new Date(runbook.updated_at).toLocaleDateString()}</span>
          {(runbook.target_services || []).length > 0 && (
            <span className="inline-flex items-center gap-1.5 flex-wrap">
              services:
              {runbook.target_services.map((s) => (
                <span key={s} className="font-mono text-xs rounded border border-border bg-secondary/60 px-1.5 py-0.5">{s}</span>
              ))}
            </span>
          )}
        </div>
      </div>

      {/* Progress */}
      <div className="rounded-xl border border-border bg-card p-4 space-y-2 no-print">
        <div className="flex items-center justify-between text-sm">
          <span className="inline-flex items-center gap-2 font-medium"><ListChecks className="h-4 w-4 text-primary" /> Execution progress</span>
          <span className="text-muted-foreground tabular-nums">{doneCount}/{steps.length} steps</span>
        </div>
        <Progress value={progress} data-testid={RUNBOOK.progressBar} className="h-2" />
      </div>

      {/* Steps */}
      <div className="space-y-4">
        <h2 className="font-display text-xl font-semibold">Procedure</h2>
        {steps.map((step, i) => {
          const isDone = !!checked[i];
          return (
            <div
              key={i}
              data-testid={RUNBOOK.step(i)}
              className={cn(
                "rounded-xl border bg-card p-5 transition-colors",
                isDone ? "border-emerald-500/40 bg-emerald-500/[0.03]" : "border-border"
              )}
            >
              <div className="flex items-start gap-3">
                <Checkbox
                  checked={isDone}
                  onCheckedChange={(v) => setChecked((c) => ({ ...c, [i]: !!v }))}
                  data-testid={RUNBOOK.stepCheck(i)}
                  className="mt-1"
                />
                <div className="flex-1 min-w-0 space-y-3">
                  <div className="flex items-baseline gap-2">
                    <span className="font-mono text-sm text-primary font-semibold">{String(i + 1).padStart(2, "0")}</span>
                    <h3 className={cn("font-semibold", isDone && "line-through text-muted-foreground")}>{step.title}</h3>
                    {isDone && <CheckCircle2 className="h-4 w-4 text-emerald-500" />}
                  </div>
                  {step.description && <p className="text-sm text-muted-foreground leading-relaxed whitespace-pre-wrap">{step.description}</p>}
                  {step.warning && (
                    <div className="flex items-start gap-2 rounded-lg border border-amber-500/30 bg-amber-500/10 p-3 text-sm text-amber-600 dark:text-amber-300">
                      <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                      <span className="whitespace-pre-wrap">{step.warning}</span>
                    </div>
                  )}
                  {step.command && <CodeBlock code={step.command} testId={RUNBOOK.copyBtn(i)} />}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Outcome recorder */}
      <div className="rounded-xl border border-border bg-card p-5 space-y-4 no-print">
        <h2 className="font-display text-lg font-semibold inline-flex items-center gap-2"><History className="h-5 w-5 text-primary" /> Record execution outcome</h2>
        <div className="flex flex-col sm:flex-row gap-3">
          <Select value={outcome} onValueChange={setOutcome}>
            <SelectTrigger className="sm:w-48" data-testid={RUNBOOK.outcomeStatus}><SelectValue /></SelectTrigger>
            <SelectContent className="bg-popover">
              {Object.entries(OUTCOME_META).map(([k, m]) => (
                <SelectItem key={k} value={k}>{m.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Textarea
            data-testid={RUNBOOK.outcomeNotes}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Notes about this run (optional)…"
            className="flex-1 min-h-[44px]"
          />
          <Button onClick={submitOutcome} disabled={savingOutcome} data-testid={RUNBOOK.outcomeSubmit}>
            {savingOutcome ? "Saving…" : "Log outcome"}
          </Button>
        </div>

        {executions.length > 0 && (
          <div data-testid={RUNBOOK.executionLog} className="divide-y divide-border border-t border-border pt-2">
            {executions.map((ex) => {
              const m = OUTCOME_META[ex.status] || OUTCOME_META.success;
              return (
                <div key={ex.id} className="flex items-start justify-between gap-4 py-2.5 text-sm">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className={cn("border text-xs", m.badge)}>{m.label}</Badge>
                      <span className="text-muted-foreground truncate">{ex.user_name}</span>
                    </div>
                    {ex.notes && <p className="text-muted-foreground mt-1 break-words">{ex.notes}</p>}
                  </div>
                  <span className="text-xs text-muted-foreground whitespace-nowrap">{new Date(ex.timestamp).toLocaleString()}</span>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <DeleteRunbookDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        runbookTitle={runbook.title}
        onConfirm={handleDelete}
        loading={deleting}
      />
    </div>
  );
}
